import { db, storage } from './firebase';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    query,
    where,
    orderBy,
    getDocs,
    serverTimestamp
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';

const COLLECTION_NAME = 'sales_records';

export const salesService = {
    // Crear nueva venta con evidencia multimedia
    createSale: async (saleData, evidenceFile) => {
        try {
            let evidenceUrl = null;

            // 1. Subir evidencia si existe
            if (evidenceFile) {
                const fileExt = evidenceFile.name.split('.').pop();
                const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
                const storageRef = ref(storage, `sales_evidence/${saleData.advisorId}/${fileName}`);

                const snapshot = await uploadBytes(storageRef, evidenceFile);
                evidenceUrl = await getDownloadURL(snapshot.ref);
            }

            // 2. Calcular valores derivados (para asegurar integridad)
            const price = parseFloat(saleData.price);
            const netAmount = price / 1.18; // Precio sin IGV
            const commissionBase = netAmount * 0.70; // Tras 30% de Impuesto a la Renta

            // Regla de comisión (sobre la base tras Imp. Renta):
            // > 100 soles -> 15%
            // <= 100 soles -> 10%
            const commissionRate = price > 100 ? 0.15 : 0.10;
            const commission = commissionBase * commissionRate;

            // 3. Guardar en Firestore
            // Remove evidenceFile from saleData if present to avoid Firestore error
            const { evidenceFile: _, ...cleanSaleData } = saleData;

            const docRef = await addDoc(collection(db, COLLECTION_NAME), {
                ...cleanSaleData,
                price: price,
                firstPayment: parseFloat(saleData.firstPayment),
                dueAmount: price - parseFloat(saleData.firstPayment),
                netAmount: netAmount,
                commissionBase: commissionBase,
                commission: commission,
                commissionRate: commissionRate,
                evidenceUrl: evidenceUrl,
                isValid: true, // Por defecto una venta es válida
                createdAt: serverTimestamp(),
                date: saleData.date // Guardamos la fecha seleccionada por el asesor
            });

            return docRef.id;
        } catch (error) {
            console.error('Error creating sale:', error);
            throw error;
        }
    },

    // Obtener ventas (opcionalmente filtradas por asesor y mes)
    getSales: async (advisorId, monthStr) => {
        try {
            // Si hay advisorId, filtramos por ese asesor. Si es null/undefined, traemos todas (para Admin)
            let constraints = [orderBy('date', 'desc')];

            if (advisorId) {
                constraints.unshift(where('advisorId', '==', advisorId));
            }

            const q = query(collection(db, COLLECTION_NAME), ...constraints);

            const snapshot = await getDocs(q);
            const sales = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filtrado por mes en cliente
            if (monthStr) {
                return sales.filter(s => s.date.startsWith(monthStr));
            }

            return sales;
        } catch (error) {
            console.error('Error fetching sales:', error);
            throw error;
        }
    },

    // Actualizar estado de validez de la venta
    updateSaleStatus: async (saleId, isValid) => {
        try {
            const saleRef = doc(db, COLLECTION_NAME, saleId);
            await updateDoc(saleRef, {
                isValid: isValid
            });
        } catch (error) {
            console.error('Error updating sale status:', error);
            throw error;
        }
    },

    // Eliminar venta y su evidencia
    deleteSale: async (saleId, evidenceUrl) => {
        try {
            // 1. Eliminar documento
            await deleteDoc(doc(db, COLLECTION_NAME, saleId));

            // 2. Eliminar imagen si existe
            if (evidenceUrl) {
                // Extraer path de la URL si es necesario, o usar ref a partir de URL
                // Firebase Storage refFromURL funciona
                const storageRef = ref(storage, evidenceUrl);
                await deleteObject(storageRef).catch(err => {
                    console.warn("Could not delete image file, might not exist:", err);
                });
            }
        } catch (error) {
            console.error('Error deleting sale:', error);
            throw error;
        }
    }
};
